import {
	InformationGatheringDestination as ApiInformationGatheringDestination,
	InformationGatheringRepresentativeRelationship,
	InformationGatheringSexAssignedAtBirth,
	type InformationGatheringSubmissionInput,
} from '@visiba/api-vcs-client-v2';

export type RepresentativeRelationship = InformationGatheringRepresentativeRelationship;
export type SexAssignedAtBirth = InformationGatheringSexAssignedAtBirth;
export type InformationGatheringDestination = 'red-robin' | 'messaging' | 'care-navigation';

const apiDestinations: Record<InformationGatheringDestination, ApiInformationGatheringDestination> = {
	'red-robin': ApiInformationGatheringDestination.RedRobin,
	messaging: ApiInformationGatheringDestination.Messaging,
	'care-navigation': ApiInformationGatheringDestination.CareNavigation,
};

export interface InformationGatheringAddress {
	addressLine1: string;
	addressLine2: string;
	townOrCity: string;
	postcode: string;
}

export interface PatientInformationDraft {
	firstName: string;
	lastName: string;
	sexAssignedAtBirth: SexAssignedAtBirth | '';
	phoneNumber: string;
	homeAddress: InformationGatheringAddress;
	currentLocationIsHomeAddress: boolean | null;
	currentLocation: InformationGatheringAddress;
}

export interface RepresentativeInformationDraft {
	relationshipToPatient: RepresentativeRelationship | '';
	phoneNumber: string;
}

export interface InformationGatheringDraft {
	patient: PatientInformationDraft;
	representative: RepresentativeInformationDraft;
}

export interface InformationGatheringPatientIdentity {
	dateOfBirth: string;
	nhsNumber: string;
}

export type InformationGatheringRole = 'self' | 'representative';

export function createEmptyAddress(): InformationGatheringAddress {
	return {
		addressLine1: '',
		addressLine2: '',
		townOrCity: '',
		postcode: '',
	};
}

export function createEmptyInformationGatheringDraft(): InformationGatheringDraft {
	return {
		patient: {
			firstName: '',
			lastName: '',
			sexAssignedAtBirth: '',
			phoneNumber: '',
			homeAddress: createEmptyAddress(),
			currentLocationIsHomeAddress: null,
			currentLocation: createEmptyAddress(),
		},
		representative: {
			relationshipToPatient: '',
			phoneNumber: '',
		},
	};
}

export function createEmptyInformationGatheringPatientIdentity(): InformationGatheringPatientIdentity {
	return {
		dateOfBirth: '',
		nhsNumber: '',
	};
}

export function normalizeUkPostcode(postcode: string): string {
	const compactPostcode = postcode.toUpperCase().replaceAll(/\s/g, '');

	if (compactPostcode.length <= 3) {
		return compactPostcode;
	}

	return `${compactPostcode.slice(0, -3)} ${compactPostcode.slice(-3)}`;
}

export function normalizeAddress(address: InformationGatheringAddress): InformationGatheringAddress {
	return {
		addressLine1: address.addressLine1.trim(),
		addressLine2: address.addressLine2.trim(),
		townOrCity: address.townOrCity.trim(),
		postcode: normalizeUkPostcode(address.postcode),
	};
}

export function isAddressComplete(address: InformationGatheringAddress): boolean {
	return address.addressLine1.trim() !== '' && address.townOrCity.trim() !== '' && address.postcode.trim() !== '';
}

export function isInformationGatheringComplete(
	draft: InformationGatheringDraft,
	role: InformationGatheringRole,
	patientIdentity: InformationGatheringPatientIdentity,
	destination: InformationGatheringDestination,
): boolean {
	const patientComplete =
		(destination === 'red-robin' || draft.patient.sexAssignedAtBirth !== '') &&
		draft.patient.phoneNumber.trim() !== '' &&
		isAddressComplete(draft.patient.currentLocation) &&
		draft.patient.currentLocationIsHomeAddress !== null &&
		(draft.patient.currentLocationIsHomeAddress || isAddressComplete(draft.patient.homeAddress));

	if (role === 'self') {
		return patientComplete;
	}

	return (
		patientComplete &&
		draft.patient.firstName.trim() !== '' &&
		draft.patient.lastName.trim() !== '' &&
		patientIdentity.nhsNumber.trim() !== '' &&
		patientIdentity.dateOfBirth !== '' &&
		draft.representative.relationshipToPatient !== '' &&
		draft.representative.phoneNumber.trim() !== ''
	);
}

function toAddressInput(address: InformationGatheringAddress) {
	return {
		AddressLine1: address.addressLine1,
		AddressLine2: address.addressLine2,
		TownOrCity: address.townOrCity,
		Postcode: address.postcode,
	};
}

export function toInformationGatheringSubmissionInput(
	draft: InformationGatheringDraft,
	role: InformationGatheringRole,
	patientIdentity: InformationGatheringPatientIdentity,
	destination: InformationGatheringDestination,
): InformationGatheringSubmissionInput {
	const representative = role === 'representative';
	const currentLocationIsHomeAddress = draft.patient.currentLocationIsHomeAddress;
	if (currentLocationIsHomeAddress === null) {
		throw new Error('A home-address answer is required before creating an information-gathering submission');
	}
	const sexAssignedAtBirth = draft.patient.sexAssignedAtBirth;
	if (sexAssignedAtBirth === '' && destination !== 'red-robin') {
		throw new Error('Sex assigned at birth is required before creating an information-gathering submission');
	}
	let representativeInput;
	if (representative) {
		const relationshipToPatient = draft.representative.relationshipToPatient;
		if (relationshipToPatient === '') {
			throw new Error('A relationship is required before creating a representative information-gathering submission');
		}
		representativeInput = {
			RelationshipToPatient: relationshipToPatient,
			PhoneNumber: draft.representative.phoneNumber,
		};
	}

	return {
		Role: role,
		Destination: apiDestinations[destination],
		Patient: {
			FirstName: representative ? draft.patient.firstName : undefined,
			LastName: representative ? draft.patient.lastName : undefined,
			DateOfBirth: representative ? patientIdentity.dateOfBirth : undefined,
			NhsNumber: representative ? patientIdentity.nhsNumber : undefined,
			SexAssignedAtBirth: sexAssignedAtBirth || undefined,
			PhoneNumber: draft.patient.phoneNumber,
			HomeAddress: currentLocationIsHomeAddress ? undefined : toAddressInput(draft.patient.homeAddress),
			CurrentLocationIsHomeAddress: currentLocationIsHomeAddress,
			CurrentLocation: toAddressInput(draft.patient.currentLocation),
		},
		Representative: representativeInput,
	};
}
